import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../AuthProvider";
import Spinner from "../../components/Spinner";
import ErrorPage from "../../components/ErrorPage";
import http from "../../api/apiClient";
import { fetchSchoolYears } from "../../utils/fetchSchoolYears";
import SchoolYearDropdown from "../../components/SchoolYearDropdown";
import { FaCalendarAlt } from "react-icons/fa";
import {
  PlacementHero,
  PlacementPageShell,
  PlacementSection,
  PlacementStat,
} from "./components/PlacementLds";

type SchoolYear = {
  id: string;
  name?: string;
  label?: string;
  startDate?: string;
  endDate?: string;
};

type PlanningChild = {
  id: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: unknown;
  dateBirth?: unknown;
  classroomName?: string | null;
  suggestedClassroomName?: string | null;
  currentClassroomName?: string | null;
  targetClassroomName?: string | null;
  reason?: string | null;
};

type FuturePlanningResponse = {
  schoolYear: SchoolYear;
  assigned: PlanningChild[];
  unassigned: PlanningChild[];
  interestList: PlanningChild[];
  grade1Candidates?: PlanningChild[];
  statistics?: {
    total: number;
    assigned: number;
    unassigned: number;
    interest: number;
    grade1Candidates?: number;
  };
};

type SchoolYearBucket = "active" | "future" | "previous";
type PlanningStatus = "needsPlacement" | "interest" | "planned" | "expected";

type PlanningTableRow = PlanningChild & {
  planningStatus: PlanningStatus;
};

const fullName = (child: PlanningChild, fallback: string) =>
  [child.firstName, child.lastName].filter(Boolean).join(" ").trim() ||
  fallback;

const parseDateSafely = (value?: unknown): Date | null => {
  if (value == null || value === "") return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  }

  if (typeof value === "number") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  // Java LocalDate is sometimes serialized as [year, month, day].
  if (Array.isArray(value)) {
    if (value.length >= 3) {
      const year = Number(value[0]);
      const month = Number(value[1]);
      const day = Number(value[2]);

      if (
        Number.isInteger(year) &&
        Number.isInteger(month) &&
        Number.isInteger(day) &&
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= 31
      ) {
        const parsed = new Date(year, month - 1, day);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }
    }

    return null;
  }

  if (typeof value === "object") {
    const dateObject = value as {
      year?: unknown;
      month?: unknown;
      monthValue?: unknown;
      day?: unknown;
      dayOfMonth?: unknown;
      date?: unknown;
      value?: unknown;
      dateOfBirth?: unknown;
      birthDate?: unknown;
    };

    const year = Number(dateObject.year);
    const month = Number(dateObject.monthValue ?? dateObject.month);
    const day = Number(dateObject.dayOfMonth ?? dateObject.day);

    if (
      Number.isInteger(year) &&
      Number.isInteger(month) &&
      Number.isInteger(day) &&
      month >= 1 &&
      month <= 12 &&
      day >= 1 &&
      day <= 31
    ) {
      const parsed = new Date(year, month - 1, day);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const candidate =
      dateObject.date ??
      dateObject.value ??
      dateObject.dateOfBirth ??
      dateObject.birthDate;

    if (candidate !== undefined && candidate !== value) {
      return parseDateSafely(candidate);
    }

    return null;
  }

  if (typeof value !== "string") return null;

  const normalized = value.trim();
  if (!normalized) return null;

  const isoDateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalized);
  if (isoDateOnly) {
    const [, year, month, day] = isoDateOnly;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const dmyDateOnly = /^(\d{2})[-/](\d{2})[-/](\d{4})$/.exec(normalized);
  if (dmyDateOnly) {
    const [, day, month, year] = dmyDateOnly;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value?: unknown) => {
  const date = parseDateSafely(value);
  if (!date) return "-";

  return new Intl.DateTimeFormat("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const getAge = (value?: unknown) => {
  const birthDate = parseDateSafely(value);
  if (!birthDate) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();

  const birthdayThisYear = new Date(
    today.getFullYear(),
    birthDate.getMonth(),
    birthDate.getDate(),
  );

  if (today < birthdayThisYear) {
    age -= 1;
  }

  return age;
};

const getSchoolYearLabel = (schoolYear: SchoolYear) => {
  if (schoolYear.name) return schoolYear.name;
  if (schoolYear.label) return schoolYear.label;

  if (schoolYear.startDate && schoolYear.endDate) {
    const startDate = parseDateSafely(schoolYear.startDate);
    const endDate = parseDateSafely(schoolYear.endDate);

    if (startDate && endDate) {
      return `${startDate.getFullYear()} - ${endDate.getFullYear()}`;
    }
  }

  return schoolYear.id;
};

const getCurrentClassroom = (child: PlanningChild) =>
  child.currentClassroomName ?? child.classroomName ?? "-";

const getSuggestedClassroom = (child: PlanningChild) =>
  child.targetClassroomName ?? child.suggestedClassroomName ?? "-";

const mergeChildWithCandidate = (
  child: PlanningChild,
  candidateById: Map<string, PlanningChild>,
): PlanningChild => {
  const candidate = candidateById.get(child.id);

  if (!candidate) return child;

  return {
    ...candidate,
    ...child,
    reason: child.reason ?? candidate.reason,
    currentClassroomName:
      child.currentClassroomName ?? candidate.currentClassroomName,
    targetClassroomName:
      child.targetClassroomName ?? candidate.targetClassroomName,
    suggestedClassroomName:
      child.suggestedClassroomName ?? candidate.suggestedClassroomName,
  };
};

const getSchoolYearRange = (schoolYear: SchoolYear) => {
  if (schoolYear.startDate && schoolYear.endDate) {
    const start = parseDateSafely(schoolYear.startDate);
    const end = parseDateSafely(schoolYear.endDate);

    if (start && end) {
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
  }

  const label = schoolYear.name ?? schoolYear.label ?? "";
  const match = label.match(/(\d{4})\s*[-/]\s*(\d{4})/);

  if (!match) return null;

  const startYear = Number(match[1]);
  const endYear = Number(match[2]);

  return {
    start: new Date(startYear, 7, 1),
    end: new Date(endYear, 6, 31, 23, 59, 59),
  };
};

const getSchoolYearBucket = (schoolYear: SchoolYear): SchoolYearBucket => {
  const range = getSchoolYearRange(schoolYear);
  const now = new Date();

  if (!range) return "future";
  if (now >= range.start && now <= range.end) return "active";
  if (range.start > now) return "future";

  return "previous";
};

const getInitialSchoolYearId = (years: SchoolYear[]) => {
  const sortedYears = [...years].sort((a, b) => {
    const aStart = getSchoolYearRange(a)?.start?.getTime() ?? 0;
    const bStart = getSchoolYearRange(b)?.start?.getTime() ?? 0;
    return aStart - bStart;
  });

  const active = sortedYears.find(
    (year) => getSchoolYearBucket(year) === "active",
  );
  if (active) return active.id;

  const future = sortedYears.find(
    (year) => getSchoolYearBucket(year) === "future",
  );
  if (future) return future.id;

  return sortedYears[sortedYears.length - 1]?.id ?? "";
};

const statusConfig: Record<
  PlanningStatus,
  {
    label: string;
    badgeClassName: string;
    leftBorderClassName: string;
  }
> = {
  needsPlacement: {
    label: "Needs placement",
    badgeClassName: "bg-yellow-50 text-yellow-800 ring-yellow-200",
    leftBorderClassName: "border-l-yellow-400",
  },
  interest: {
    label: "Interest request",
    badgeClassName: "bg-blue-50 text-blue-700 ring-blue-200",
    leftBorderClassName: "border-l-blue-400",
  },
  planned: {
    label: "Planned",
    badgeClassName: "bg-green-50 text-green-700 ring-green-200",
    leftBorderClassName: "border-l-green-400",
  },
  expected: {
    label: "Expected",
    badgeClassName: "bg-gray-100 text-gray-700 ring-gray-200",
    leftBorderClassName: "border-l-gray-300",
  },
};

const StatPill: React.FC<{
  label: string;
  value: number;
  className: string;
}> = ({ label, value, className }) => (
  <div
    className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm ${className}`}
  >
    <span>{label}</span>
    <span className="font-semibold">{value}</span>
  </div>
);

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
    {message}
  </div>
);

const PlanningTable: React.FC<{
  rows: PlanningTableRow[];
  emptyMessage: string;
}> = ({ rows, emptyMessage }) => {
  const { t } = useTranslation();

  if (rows.length === 0) {
    return <EmptyState message={emptyMessage} />;
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <table className="min-w-full text-left">
        <thead className="border-b border-gray-200 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:bg-gray-900/60 dark:text-gray-400">
          <tr>
            <th className="px-4 py-3">
              {t("futurePlanning.table.child", { defaultValue: "Child" })}
            </th>
            <th className="px-4 py-3">
              {t("futurePlanning.table.birthDate", {
                defaultValue: "Birth date",
              })}
            </th>
            <th className="px-4 py-3">
              {t("futurePlanning.table.currentClass", {
                defaultValue: "Current class",
              })}
            </th>
            <th className="px-4 py-3">
              {t("futurePlanning.table.advice", { defaultValue: "Advice" })}
            </th>
            <th className="px-4 py-3">
              {t("futurePlanning.table.reason", { defaultValue: "Reason" })}
            </th>
            <th className="px-4 py-3">
              {t("futurePlanning.table.status", { defaultValue: "Status" })}
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {rows.map((child) => {
            const status = statusConfig[child.planningStatus];

            return (
              <tr
                key={`${child.planningStatus}-${child.id}`}
                className={`border-l-4 ${status.leftBorderClassName} bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700/40`}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {fullName(
                        child,
                        t("futurePlanning.table.unknownChild", {
                          defaultValue: "Unknown child",
                        }),
                      )}
                    </span>

                    {child.committed === true && (
                      <span className="inline-flex rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-green-200 dark:bg-green-900/20 dark:text-green-300 dark:ring-green-800">
                        {t("futurePlanning.commitment.yes", {
                          defaultValue: "Committed",
                        })}
                      </span>
                    )}

                    {child.committed === false && (
                      <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:ring-gray-700">
                        {t("futurePlanning.commitment.no", {
                          defaultValue: "No commitment",
                        })}
                      </span>
                    )}

                    {child.committed === null && (
                      <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:ring-gray-700">
                        {t("futurePlanning.commitment.no", {
                          defaultValue: "No commitment",
                        })}
                      </span>
                    )}
                  </div>
                </td>

                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                  {(() => {
                    const value = child.dateOfBirth ?? child.dateBirth;
                    const age = getAge(value);

                    return (
                      <>
                        {formatDate(value)}
                        {age !== null && (
                          <span className="ml-1 font-semibold text-gray-900 dark:text-gray-100">
                            ({age})
                          </span>
                        )}
                      </>
                    );
                  })()}
                </td>

                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                  {getCurrentClassroom(child)}
                </td>

                <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                  {getSuggestedClassroom(child)}
                </td>

                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                  {child.reason || "-"}
                </td>

                <td className="px-4 py-3">
                  <span
                    className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${status.badgeClassName}`}
                  >
                    {t(`futurePlanning.status.${child.planningStatus}`, {
                      defaultValue: status.label,
                    })}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const FuturePlanning: React.FC = () => {
  const { t } = useTranslation();
  const { keycloak } = useContext(AuthContext);

  const [schoolYears, setSchoolYears] = useState<SchoolYear[]>([]);
  const [selectedSchoolYearId, setSelectedSchoolYearId] = useState("");
  const [planning, setPlanning] = useState<FuturePlanningResponse | null>(null);

  const [loadingSchoolYears, setLoadingSchoolYears] = useState(true);
  const [loadingPlanning, setLoadingPlanning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const candidateById = useMemo(() => {
    const map = new Map<string, PlanningChild>();

    (planning?.grade1Candidates ?? []).forEach((child) => {
      map.set(child.id, child);
    });

    return map;
  }, [planning?.grade1Candidates]);

  const assignedChildren = useMemo(
    () =>
      (planning?.assigned ?? []).map((child) =>
        mergeChildWithCandidate(child, candidateById),
      ),
    [planning?.assigned, candidateById],
  );

  const unassignedChildren = useMemo(
    () =>
      (planning?.unassigned ?? []).map((child) =>
        mergeChildWithCandidate(child, candidateById),
      ),
    [planning?.unassigned, candidateById],
  );

  const interestListChildren = useMemo(
    () =>
      (planning?.interestList ?? []).map((child) =>
        mergeChildWithCandidate(child, candidateById),
      ),
    [planning?.interestList, candidateById],
  );

  const remainingCandidates = useMemo(() => {
    if (!planning?.grade1Candidates) return [];

    const visibleIds = new Set([
      ...assignedChildren.map((child) => child.id),
      ...unassignedChildren.map((child) => child.id),
      ...interestListChildren.map((child) => child.id),
    ]);

    return planning.grade1Candidates.filter(
      (child) => !visibleIds.has(child.id),
    );
  }, [
    planning?.grade1Candidates,
    assignedChildren,
    unassignedChildren,
    interestListChildren,
  ]);

  const allRows: PlanningTableRow[] = useMemo(
    () => [
      ...unassignedChildren.map((child) => ({
        ...child,
        planningStatus: "needsPlacement" as PlanningStatus,
      })),
      ...interestListChildren.map((child) => ({
        ...child,
        planningStatus: "interest" as PlanningStatus,
      })),
      ...assignedChildren.map((child) => ({
        ...child,
        planningStatus: "planned" as PlanningStatus,
      })),
      ...remainingCandidates.map((child) => ({
        ...child,
        planningStatus: "expected" as PlanningStatus,
      })),
    ],
    [
      unassignedChildren,
      interestListChildren,
      assignedChildren,
      remainingCandidates,
    ],
  );

  const statistics = useMemo(() => {
    if (!planning) {
      return {
        expected: 0,
        assigned: 0,
        unassigned: 0,
        interest: 0,
      };
    }

    return {
      expected:
        planning.statistics?.grade1Candidates ??
        planning.grade1Candidates?.length ??
        0,
      assigned: planning.statistics?.assigned ?? planning.assigned.length,
      unassigned: planning.statistics?.unassigned ?? planning.unassigned.length,
      interest: planning.statistics?.interest ?? planning.interestList.length,
    };
  }, [planning]);

  useEffect(() => {
    const loadSchoolYears = async () => {
      try {
        if (!keycloak?.token) return;

        setLoadingSchoolYears(true);
        setError(null);

        const years = await fetchSchoolYears();
        const safeYears = Array.isArray(years) ? years : [];

        setSchoolYears(safeYears);

        if (safeYears.length > 0) {
          setSelectedSchoolYearId(getInitialSchoolYearId(safeYears));
        }
      } catch (err) {
        console.error(err);
        setError(
          t("futurePlanning.errors.schoolYears", {
            defaultValue: "Unable to load school years.",
          }),
        );
      } finally {
        setLoadingSchoolYears(false);
      }
    };

    loadSchoolYears();
  }, [keycloak?.token, t]);

  useEffect(() => {
    if (!selectedSchoolYearId || !keycloak?.token) return;

    const fetchPlanning = async () => {
      try {
        setLoadingPlanning(true);
        setError(null);

        const response = await http.get<FuturePlanningResponse>(
          "/planning/first-grade",
          {
            params: {
              schoolYearId: selectedSchoolYearId,
            },
          },
        );

        setPlanning({
          ...response.data,
          assigned: response.data?.assigned ?? [],
          unassigned: response.data?.unassigned ?? [],
          interestList: response.data?.interestList ?? [],
          grade1Candidates: response.data?.grade1Candidates ?? [],
        });
      } catch (err) {
        console.error(err);
        setPlanning(null);
        setError(
          t("futurePlanning.errors.planning", {
            defaultValue: "Unable to load future planning.",
          }),
        );
      } finally {
        setLoadingPlanning(false);
      }
    };

    fetchPlanning();
  }, [selectedSchoolYearId, keycloak?.token, t]);

  if (loadingSchoolYears) {
    return (
      <PlacementPageShell>
        <Spinner />
      </PlacementPageShell>
    );
  }

  if (error && !planning) {
    return <ErrorPage status={500} message={error} />;
  }

  return (
    <>
      <style>{`
        @keyframes lds-page-enter {
          from {
            opacity: 0;
            transform: translateY(14px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes lds-content-enter {
          from {
            opacity: 0;
            transform: translateY(18px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .lds-page-enter {
          animation: lds-page-enter 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .lds-page-enter > * {
          animation: lds-content-enter 480ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .lds-page-enter > *:nth-child(2) {
          animation-delay: 45ms;
        }

        .lds-page-enter > *:nth-child(3) {
          animation-delay: 90ms;
        }

        .lds-page-enter > *:nth-child(4) {
          animation-delay: 135ms;
        }

        @media (prefers-reduced-motion: reduce) {
          .lds-page-enter,
          .lds-page-enter > * {
            animation: none;
          }
        }
      `}</style>
      <div className="lds-page-enter">
    <PlacementPageShell>
      <PlacementHero
        eyebrow={t("placement.overview.title", { defaultValue: "Placement" })}
        title={t("futurePlanning.title", { defaultValue: "Future planning" })}
        description={t("futurePlanning.description", {
          defaultValue:
            "Review children expected to move to the next school year.",
        })}
        icon={<FaCalendarAlt aria-hidden="true" />}
        illustration={<FaCalendarAlt aria-hidden="true" />}
        actions={
          <SchoolYearDropdown
            schoolYears={schoolYears}
            selectedSchoolYearId={selectedSchoolYearId}
            onChange={setSelectedSchoolYearId}
          />
        }
      />
      <div className="space-y-5">
        <PlacementSection className="p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {t("futurePlanning.heading", {
                  defaultValue: "First grade preparation",
                })}
              </h1>
              <p className="mt-1 max-w-4xl text-sm text-gray-500 dark:text-gray-400">
                {t("futurePlanning.description", {
                  defaultValue:
                    "Review which children are expected to move to first grade and which placements still need attention.",
                })}
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                <StatPill
                  label={t("futurePlanning.stats.expected", {
                    defaultValue: "Expected first grade",
                  })}
                  value={statistics.expected}
                  className="bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200"
                />
                <StatPill
                  label={t("futurePlanning.stats.unassigned", {
                    defaultValue: "Needs placement",
                  })}
                  value={statistics.unassigned}
                  className="bg-yellow-50 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300"
                />
                <StatPill
                  label={t("futurePlanning.stats.interest", {
                    defaultValue: "Interest list",
                  })}
                  value={statistics.interest}
                  className="bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
                />
                <StatPill
                  label={t("futurePlanning.stats.assigned", {
                    defaultValue: "Already planned",
                  })}
                  value={statistics.assigned}
                  className="bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300"
                />
              </div>
            </div>
          </div>
        </PlacementSection>

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <PlacementStat
            label={t("futurePlanning.stats.expected", {
              defaultValue: "Expected first grade",
            })}
            value={statistics.expected}
          />
          <PlacementStat
            label={t("futurePlanning.stats.unassigned", {
              defaultValue: "Needs placement",
            })}
            value={statistics.unassigned}
          />
          <PlacementStat
            label={t("futurePlanning.stats.interest", {
              defaultValue: "Interest list",
            })}
            value={statistics.interest}
          />
          <PlacementStat
            label={t("futurePlanning.stats.assigned", {
              defaultValue: "Already planned",
            })}
            value={statistics.assigned}
          />
        </div>

        {loadingPlanning && <Spinner />}

        {!loadingPlanning && planning && (
          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="mb-4 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t("futurePlanning.sections.overview", {
                    defaultValue: "First grade overview",
                  })}
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {t("futurePlanning.sections.overviewDescription", {
                    defaultValue:
                      "All children expected for first grade, grouped by status.",
                  })}
                </p>
              </div>

              <span className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-sm font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
                {allRows.length}
              </span>
            </div>

            <PlanningTable
              rows={allRows}
              emptyMessage={t("futurePlanning.empty.overview", {
                defaultValue: "No children found.",
              })}
            />
          </section>
        )}
      </div>
    </PlacementPageShell>

      </div>
    </>
  );
};

export default FuturePlanning;
